<template>
  <div class="bg-white rounded-lg hover:shadow-lg overflow-hidden my-4 w-full relative">
    <div class="flex flex-row gap-2 ">
      <!-- Smaller thumbnail image -->
      <img :src="audio.thumbnail_url" :alt="audio.title" class="h-full max-h-40 object-cover" />

      <div class="flex flex-col flex-1 py-2 px-2">
        <div
          class="flex flex-row justify-between items-center flex-wrap text-xs mb-2 w-full"
        >
          <div
            v-if="audio.episode_number"
            class="text-[10px] text-gray-500 border border-gray-500 rounded-full px-2 py-0.5"
          >
            {{ audio.episode_number }}
          </div>
          <div
            class="flex flex-row justify-end gap-1 text-gray-500 px-2 py-0.5 text-[10px]"
          >
            <img src="~/assets/img/Calendar.svg" alt="Calendar" class="hidden w-3 h-3" />
            <p>
              {{
                new Date(audio.created_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric"
                })
              }}
            </p>
          </div>
        </div>

        <!-- Title and subtitle in compact form -->
        <h1 class="text-sm font-bold text-brand">
          {{ audio.title }}
        </h1>
        <h2 class="text-slate-500 font-medium text-xs">
          {{ audio.subtitle }}
        </h2>

        <!-- Add this description section -->
        <div class="hidden">
          <p
            v-if="audio.description"
            class="text-xs text-slate-700"
            :class="{ 'line-clamp-2': !isDescriptionExpanded }"
          >
            {{ audio.description }}
          </p>
          <button
            v-if="audio.description"
            @click="toggleDescription"
            class="text-xs text-brand font-medium mt-1"
          >
            {{ isDescriptionExpanded ? "Show less" : "Show more" }}
          </button>
        </div>

        <!-- Add episode, date and categories in a compact format -->
        <div
          class="flex flex-row gap-1 items-center flex-wrap text-xs mt-2 w-full justify-end"
        >
          <div
            v-for="(cat, index) in audio.categories"
            :key="index"
            class="text-brand border border-brand rounded-full px-2 py-0.5 text-[10px]"
          >
            {{ cat }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="audioError" class="mt-2 p-1 bg-red-100 text-red-700 rounded text-xs">
      Error: {{ audioError }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
// Import the required images
import downloadIcon from "~/assets/img/Download.svg";
import shareIcon from "~/assets/img/Share.svg";
import expandIcon from "~/assets/img/Expand screen.svg";
import playIcon from "~/assets/img/play=true.svg";
import pauseIcon from "~/assets/img/play=false.svg";
import rewindIcon from "~/assets/img/Rewind.svg";

const props = defineProps({
  audio: {
    type: Object,
    required: true
  }
});

// Computed property to handle backward compatibility

const isPlaying = ref(false);
const progress = ref(0);
const currentTime = ref("00:00");
const duration = ref("00:00");
const lastPlaybackPosition = ref(0); // Store the last playback position
const playPauseIcon = computed(() => (isPlaying.value ? pauseIcon : playIcon));
const audioError = ref(null);
const progressBar = ref(null);
const isDescriptionExpanded = ref(false);

// Toggle description expansion
const toggleDescription = () => {
  isDescriptionExpanded.value = !isDescriptionExpanded.value;
};

// Format seconds to MM:SS format
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

// Convert timestamp string (MM:SS) to seconds
const timestampToSeconds = (timestamp) => {
  if (!timestamp || timestamp === "") return 0;

  const parts = timestamp.split(":");
  if (parts.length !== 2) return 0;

  const minutes = parseInt(parts[0], 10);
  const seconds = parseInt(parts[1], 10);

  if (isNaN(minutes) || isNaN(seconds)) return 0;

  return minutes * 60 + seconds;
};
</script>

<style scoped></style>
